"use client"

// PromptInput: the composer. Adapted from Vercel AI Elements
// (https://ai-sdk.dev/elements, prompt-input), re-authored token-driven and
// without the AI-SDK message-shape coupling — it is a plain controlled form so
// the existing ChatInterface submit/stream logic is unchanged.

import { Square } from "lucide-react"
import { cn } from "@/lib/utils"
import { forwardRef, type ComponentProps, type KeyboardEvent, type ReactNode } from "react"

/**
 * Composer container (a `<form>`).
 *
 * @param onSubmit - submit handler for the form.
 * @param className - extra classes merged onto the form.
 */
export function PromptInput({ className, ...props }: ComponentProps<"form">): ReactNode {
  return (
    <form
      className={cn(
        "flex w-full flex-col gap-2 rounded-3xl border border-composer-border bg-composer-bg px-2 py-1.5 transition focus-within:border-composer-border-focus",
        className
      )}
      {...props}
    />
  )
}

/**
 * A single-row of composer controls (attach | textarea | send), meant to sit
 * inside PromptInput below any attachment previews.
 */
export function PromptInputRow({ className, ...props }: ComponentProps<"div">): ReactNode {
  return <div className={cn("flex w-full items-end gap-1", className)} {...props} />
}

interface PromptInputTextareaProps extends Omit<ComponentProps<"textarea">, "onChange"> {
  /** Called with the textarea value on every change. */
  onValueChange: (value: string) => void
  /** Called when Enter (without Shift) is pressed and there is text. */
  onSubmitShortcut: () => void
}

/**
 * Auto-sizing textarea with Enter-to-send / Shift+Enter-for-newline. Rendered as
 * a clean native textarea (no border/background of its own) so it reads as part
 * of the composer surface, not a nested box.
 *
 * @param value - the controlled text value.
 * @param onValueChange - receives the new value on change.
 * @param onSubmitShortcut - invoked on the Enter-to-send shortcut.
 */
export const PromptInputTextarea = forwardRef<HTMLTextAreaElement, PromptInputTextareaProps>(
  function PromptInputTextarea(
    { value, onValueChange, onSubmitShortcut, className, ...props },
    ref
  ): ReactNode {
    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
      // Enter sends; Shift+Enter inserts a newline (the platform-standard chat
      // affordance). Only send when there is non-whitespace text.
      if (event.key === "Enter" && !event.shiftKey) {
        if (typeof value === "string" && value.trim()) {
          event.preventDefault()
          onSubmitShortcut()
        }
      }
    }
    return (
      <textarea
        ref={ref}
        value={value}
        onChange={e => onValueChange(e.target.value)}
        onKeyDown={handleKeyDown}
        rows={1}
        className={cn(
          "max-h-[200px] min-h-[24px] w-full resize-none border-0 bg-transparent px-3 py-2 font-body text-sm text-foreground placeholder:text-composer-placeholder outline-none focus:outline-none focus-visible:outline-none [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          className
        )}
        {...props}
      />
    )
  }
)

/**
 * Footer row of the composer (tools on the left, submit on the right).
 *
 * @param className - extra classes merged onto the footer.
 */
export function PromptInputFooter({ className, ...props }: ComponentProps<"div">): ReactNode {
  return (
    <div className={cn("flex items-center justify-between gap-2 px-1", className)} {...props} />
  )
}

/**
 * Left-aligned group for composer tool buttons.
 *
 * @param className - extra classes merged onto the group.
 */
export function PromptInputTools({ className, ...props }: ComponentProps<"div">): ReactNode {
  return <div className={cn("flex items-center gap-1.5", className)} {...props} />
}

export type PromptInputStatus = "ready" | "submitted" | "streaming" | "error"

interface PromptInputSubmitProps extends ComponentProps<"button"> {
  /** Drives the icon/label; "submitted"/"streaming" show the stop control. */
  status: PromptInputStatus
  /** Accessible Spanish label for the submit control. */
  label: string
  /** When generating, clicking the control calls this to cancel the request. */
  onStop?: () => void
  /** Accessible Spanish label for the stop control. */
  stopLabel?: string
}

/**
 * Submit control that turns into a Stop control while a response is generating,
 * so the user can cancel a long-running turn.
 *
 * @param status - the current request status.
 * @param label - accessible Spanish label for submit.
 * @param onStop - cancels the in-flight request (shown while busy).
 * @param stopLabel - accessible Spanish label for the stop control.
 */
export function PromptInputSubmit({
  status,
  label,
  onStop,
  stopLabel,
  className,
  children,
  ...props
}: PromptInputSubmitProps): ReactNode {
  const busy = status === "submitted" || status === "streaming"

  // While generating, expose a Stop button (a plain button, not a submit) so the
  // user can abort the stream instead of waiting out a long silent gap.
  if (busy && onStop) {
    return (
      <button
        type="button"
        onClick={onStop}
        aria-label={stopLabel ?? label}
        title={stopLabel ?? label}
        className={cn(
          "inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-foreground text-background transition-colors hover:bg-foreground/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring dark:bg-primary-foreground dark:text-primary dark:hover:bg-primary-foreground/90",
          className
        )}
      >
        <Square className="size-4 fill-current" aria-hidden="true" />
      </button>
    )
  }

  return (
    <button
      type="submit"
      aria-label={label}
      title={label}
      className={cn(
        "inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-foreground text-background transition-colors hover:bg-foreground/80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:bg-composer-send-disabled disabled:text-background dark:bg-primary-foreground dark:text-primary dark:hover:bg-primary-foreground/80",
        className
      )}
      {...props}
    >
      {children}
    </button>
  )
}
